const NO_IMAGE_MSG = "No image data found in the AI response.";
const CDN_HOST = "https://cdn2.tappya.com/";
const STORAGE_OBJECT_MARKER = "/o/";
const THUMB_SUFFIX_RE = /_\d+x\d+(?=(\.[^./\\]+)?$)/;

export type ImageThumbnailSize = "200x200" | "400x400" | `${number}x${number}`;

export function normalizeImageUrl(url?: string | null): string | null {
  if (!url) return null;
  const trimmed = url.trim();
  if (!trimmed || trimmed.toLowerCase() === NO_IMAGE_MSG.toLowerCase()) {
    return null;
  }
  // Serve originals through the CDN too. The thumbnail path already targets
  // CDN_HOST, but the FoodImg 404 fallback used to hand back the raw
  // firebasestorage URL, which bypasses Cloud CDN and is billed as egress on
  // every render. The -cdn bucket is a live replica of firebasestorage.app.
  return toCdnUrl(trimmed);
}

function toCdnUrl(url: string): string {
  const { base, query } = splitQuery(url);
  const storageIdx = base.indexOf(STORAGE_OBJECT_MARKER);
  if (storageIdx === -1) return url;
  try {
    const encodedPath = base.slice(storageIdx + STORAGE_OBJECT_MARKER.length);
    const decodedPath = decodeURIComponent(encodedPath);
    if (!decodedPath) return url;
    return appendQuery(`${CDN_HOST}${encodeURIComponent(decodedPath)}`, query);
  } catch {
    return url;
  }
}

function splitQuery(url: string): { base: string; query: string } {
  const idx = url.indexOf("?");
  if (idx === -1) return { base: url, query: "" };
  return { base: url.slice(0, idx), query: url.slice(idx + 1) };
}

function appendQuery(base: string, query: string): string {
  return query ? `${base}?${query}` : base;
}

function thumbnailPath(decodedPath: string, size: ImageThumbnailSize): string | null {
  const segments = decodedPath.split("/").filter(Boolean);
  const filename = segments.pop();
  if (!filename) return null;

  if (segments[segments.length - 1] === "thumbnails") {
    segments.pop();
  }

  const cleanFilename = filename.replace(THUMB_SUFFIX_RE, "");
  const dotIdx = cleanFilename.lastIndexOf(".");
  const nameOnly = dotIdx === -1 ? cleanFilename : cleanFilename.slice(0, dotIdx);
  const ext = dotIdx === -1 ? "" : cleanFilename.slice(dotIdx);
  if (!nameOnly) return null;

  segments.push("thumbnails", `${nameOnly}_${size}${ext}`);
  return segments.join("/");
}

export function imageThumbnailUrl(
  originalUrl?: string | null,
  size: ImageThumbnailSize = "400x400",
): string | null {
  const normalized = normalizeImageUrl(originalUrl);
  if (!normalized) return null;

  try {
    const { base, query } = splitQuery(normalized);
    const storageIdx = base.indexOf(STORAGE_OBJECT_MARKER);

    if (storageIdx !== -1) {
      const encodedPath = base.slice(storageIdx + STORAGE_OBJECT_MARKER.length);
      const decodedPath = decodeURIComponent(encodedPath);
      const thumbPath = thumbnailPath(decodedPath, size);
      if (!thumbPath) return appendQuery(normalized, "");
      return appendQuery(`${CDN_HOST}${encodeURIComponent(thumbPath)}`, query);
    }

    if (base.startsWith(CDN_HOST)) {
      const encodedPath = base.slice(CDN_HOST.length);
      const decodedPath = decodeURIComponent(encodedPath);
      const thumbPath = thumbnailPath(decodedPath, size);
      if (!thumbPath) return normalized;
      return appendQuery(`${CDN_HOST}${encodeURIComponent(thumbPath)}`, query);
    }
  } catch {
    return normalized;
  }

  return normalized;
}
